import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Trash2, Building2, Film, FileCode, File, ListChecks, Shield, ExternalLink, Download, Link2 } from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import EntityDetailModal, { IdLink } from './EntityDetailModal';

const ENTITY_TYPES = [
  { key: 'organizations', label: 'Organizations', prefix: 'ORG', icon: Building2, fields: ['name', 'type', 'industry', 'email', 'website'] },
  { key: 'projects', label: 'Projects', prefix: 'PRJ', icon: Film, fields: ['title', 'type', 'status', 'budget'] },
  { key: 'scripts', label: 'Scripts', prefix: 'SCR', icon: FileCode, fields: ['title', 'version', 'status', 'project_id'] },
  { key: 'files', label: 'Files', prefix: 'FIL', icon: File, fields: ['name', 'type', 'size_bytes'] },
  { key: 'lists', label: 'Lists', prefix: 'LST', icon: ListChecks, fields: ['title', 'type', 'visibility'] },
  { key: 'links', label: 'Links', prefix: 'LNK', icon: Link2, fields: ['title', 'link_type', 'source_id', 'target_id'] },
  { key: 'registry', label: 'Registry', prefix: 'REG', icon: Shield, fields: [] },
];

const EntitiesPanel = () => {
  const [activeEntity, setActiveEntity] = useState('organizations');
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [registryData, setRegistryData] = useState([]);
  const [registryTotal, setRegistryTotal] = useState(0);
  const [detailId, setDetailId] = useState(null);

  const currentType = ENTITY_TYPES.find(e => e.key === activeEntity);
  const isRegistry = activeEntity === 'registry';
  const isEnriched = ['projects', 'scripts', 'lists', 'files', 'links'].includes(activeEntity);

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      if (isRegistry) {
        const { data: res } = await apiClient.get(`/admin/registry${search ? `?search=${search}` : ''}`);
        setRegistryData(res.items || []);
        setRegistryTotal(res.total || 0);
      } else if (isEnriched) {
        const params = search ? `?search=${search}` : '';
        const { data: res } = await apiClient.get(`/admin/entities/${activeEntity}${params}`);
        setData(res.items || []);
        setTotal(res.total || 0);
      } else {
        const params = search ? `?search=${search}` : '';
        const { data: res } = await apiClient.get(`/${activeEntity}${params}`);
        setData(res.items || []);
        setTotal(res.total || 0);
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [activeEntity, search, isRegistry, isEnriched]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const handleDelete = async (ainaaId) => {
    if (!window.confirm('Delete this entity?')) return;
    try {
      await apiClient.delete(`/${activeEntity}/${ainaaId}`);
      toast.success('Deleted');
      fetchEntities();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownloadCert = async (registryId) => {
    try {
      const res = await apiClient.get(`/registry/certificate/${registryId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AINAA-Certificate-${registryId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch { toast.error('Failed to generate certificate'); }
  };

  const openDetail = (id) => setDetailId(id);

  return (
    <div>
      {/* Entity sub-tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {ENTITY_TYPES.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => { setActiveEntity(key); setSearch(''); setShowCreate(false); }}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all ${activeEntity === key ? 'bg-[#C4A35A] text-[#0F1B2D]' : 'border border-[#D8D3CB] text-[#9B9589] hover:border-[#C4A35A]'}`}
            data-testid={`entity-tab-${key}`}>
            <Icon size={12} />{label}
          </button>
        ))}
      </div>

      {/* Registry View */}
      {isRegistry ? (
        <RegistryTable
          data={registryData} total={registryTotal} loading={loading}
          search={search} setSearch={setSearch}
          onDownloadCert={handleDownloadCert} onOpenDetail={openDetail}
        />
      ) : (
        <>
          {/* Search + Create */}
          <div className="flex gap-3 mb-5 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9589]" size={16} />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${currentType?.label}...`} className="input-ainaa pl-11 h-10" data-testid="entity-search" />
            </div>
            <button onClick={() => setShowCreate(!showCreate)}
              className="btn-ainaa-primary px-4 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-2" data-testid="entity-create-btn">
              <Plus size={14} />New {currentType?.prefix}
            </button>
            <span className="self-center text-xs text-[#9B9589] font-medium">{total} total</span>
          </div>

          {showCreate && <CreateForm entityType={activeEntity} fields={currentType?.fields || []} onCreated={() => { setShowCreate(false); fetchEntities(); }} />}

          {/* Entity-specific tables */}
          {activeEntity === 'projects' && <ProjectsTable data={data} loading={loading} onDelete={handleDelete} onOpenDetail={openDetail} />}
          {activeEntity === 'scripts' && <ScriptsTable data={data} loading={loading} onDelete={handleDelete} onOpenDetail={openDetail} />}
          {activeEntity === 'lists' && <ListsTable data={data} loading={loading} onDelete={handleDelete} onOpenDetail={openDetail} />}
          {activeEntity === 'files' && <FilesTable data={data} loading={loading} onDelete={handleDelete} onOpenDetail={openDetail} />}
          {activeEntity === 'organizations' && <OrgsTable data={data} loading={loading} onDelete={handleDelete} onOpenDetail={openDetail} />}
          {activeEntity === 'links' && <LinksTable data={data} loading={loading} onDelete={handleDelete} onOpenDetail={openDetail} />}
        </>
      )}

      {/* Entity Detail Modal */}
      {detailId && (
        <EntityDetailModal
          ainaaId={detailId}
          onClose={() => setDetailId(null)}
          onNavigate={(id) => setDetailId(id)}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   REGISTRY — clean card layout
   ═══════════════════════════════════════════ */
const RegistryTable = ({ data, total, loading, search, setSearch, onDownloadCert, onOpenDetail }) => (
  <div>
    <div className="flex gap-3 mb-5 items-center">
      <div className="relative flex-1 min-w-[200px] max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9589]" size={16} />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search registry entries..." className="input-ainaa pl-11 h-10" data-testid="registry-admin-search" />
      </div>
      <span className="text-xs text-[#9B9589] font-medium">{total} entries</span>
    </div>
    {loading ? (
      <div className="text-center py-16 text-[#9B9589]">Loading...</div>
    ) : data.length === 0 ? (
      <div className="text-center py-16 text-[#9B9589]">No registry entries found</div>
    ) : (
      <div className="space-y-2" data-testid="registry-admin-table">
        {data.map((item) => (
          <div
            key={item.registry_id}
            onClick={() => onOpenDetail(item.registry_id)}
            className="border border-[#D8D3CB] bg-white px-5 py-4 cursor-pointer hover:border-[#C4A35A] transition-colors group"
            data-testid={`registry-row-${item.registry_id}`}
          >
            {/* Row 1: ID + Type badge + IP status + cert */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono font-bold text-[#C4A35A]">{item.registry_id}</span>
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">{item.work_type}</span>
                {item.ip_transfer ? (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">IP Transferred</span>
                ) : (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-gray-50 text-gray-500 border border-gray-200">Original Owner</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-[#9B9589]">{item.registered_at ? new Date(item.registered_at).toLocaleDateString() : ''}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onDownloadCert(item.registry_id); }}
                  className="p-1.5 text-[#D8D3CB] group-hover:text-[#C4A35A] transition-colors"
                  data-testid={`cert-${item.registry_id}`} title="Download Certificate"
                >
                  <Download size={14} />
                </button>
              </div>
            </div>
            {/* Row 2: Title */}
            <h4 className="text-sm font-semibold text-[#0F1B2D] mb-2">{item.title}</h4>
            {/* Row 3: Owner + Entity link */}
            <div className="flex items-center gap-6 text-xs" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-wider text-[#9B9589] font-bold">Owner</span>
                <span className="text-[#4A4640]">{item.owner_name || '—'}</span>
                <IdLink id={item.owner_ainaa_id} onClick={onOpenDetail} />
              </div>
              {item.entity_ainaa_id && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-wider text-[#9B9589] font-bold">Entity</span>
                  <IdLink id={item.entity_ainaa_id} onClick={onOpenDetail} />
                </div>
              )}
              {item.ip_transfer?.transferred_to && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-wider text-[#9B9589] font-bold">Transferred To</span>
                  <span className="text-[#4A4640]">{item.ip_transfer.transferred_to_name || ''}</span>
                  <IdLink id={item.ip_transfer.transferred_to} onClick={onOpenDetail} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════
   PROJECTS TABLE — with owner + script IDs
   ═══════════════════════════════════════════ */
const ProjectsTable = ({ data, loading, onDelete, onOpenDetail }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="entity-table-projects">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <Th>AINAA ID</Th>
          <Th>Title</Th>
          <Th className="hidden sm:table-cell">Status</Th>
          <Th className="hidden md:table-cell">Owner (Org)</Th>
          <Th className="hidden lg:table-cell">Scripts</Th>
          <Th className="hidden lg:table-cell">Budget</Th>
          <Th align="right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={7} className="text-center py-12 text-[#9B9589]">Loading...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={7} className="text-center py-12 text-[#9B9589]">No Projects found</td></tr>
        ) : data.map((item) => (
          <tr key={item.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer" onClick={() => onOpenDetail(item.ainaa_id)} data-testid={`project-row-${item.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{item.ainaa_id}</span>
            </td>
            <td className="px-4 py-3"><span className="text-sm font-semibold text-[#0F1B2D]">{item.title}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <StatusPill status={item.status} />
            </td>
            <td className="px-4 py-3 hidden md:table-cell" onClick={e => e.stopPropagation()}>
              {item.owner_id ? (
                <div>
                  <IdLink id={item.owner_id} onClick={onOpenDetail} />
                  {item._owner_name && <span className="block text-[10px] text-[#9B9589]">{item._owner_name}</span>}
                </div>
              ) : <span className="text-[10px] text-[#D8D3CB]">—</span>}
            </td>
            <td className="px-4 py-3 hidden lg:table-cell" onClick={e => e.stopPropagation()}>
              {item._scripts?.length > 0 ? (
                <div className="space-y-1">
                  {item._scripts.map(s => (
                    <IdLink key={s.ainaa_id} id={s.ainaa_id} onClick={onOpenDetail} />
                  ))}
                </div>
              ) : <span className="text-[10px] text-[#D8D3CB]">—</span>}
            </td>
            <td className="px-4 py-3 hidden lg:table-cell">
              <span className="text-xs text-[#4A4640]">{item.budget ? `INR ${(item.budget / 100000).toFixed(1)}L` : '—'}</span>
            </td>
            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(item.ainaa_id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-entity-${item.ainaa_id}`}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════════════════
   SCRIPTS TABLE — with project + owner
   ═══════════════════════════════════════════ */
const ScriptsTable = ({ data, loading, onDelete, onOpenDetail }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="entity-table-scripts">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <Th>AINAA ID</Th>
          <Th>Title</Th>
          <Th className="hidden sm:table-cell">Version</Th>
          <Th className="hidden sm:table-cell">Status</Th>
          <Th className="hidden md:table-cell">Project</Th>
          <Th className="hidden lg:table-cell">Author</Th>
          <Th align="right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={7} className="text-center py-12 text-[#9B9589]">Loading...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={7} className="text-center py-12 text-[#9B9589]">No Scripts found</td></tr>
        ) : data.map((item) => (
          <tr key={item.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer" onClick={() => onOpenDetail(item.ainaa_id)} data-testid={`script-row-${item.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{item.ainaa_id}</span>
            </td>
            <td className="px-4 py-3"><span className="text-sm font-semibold text-[#0F1B2D]">{item.title}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell"><span className="text-xs text-[#4A4640]">{item.version}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell"><StatusPill status={item.status} /></td>
            <td className="px-4 py-3 hidden md:table-cell" onClick={e => e.stopPropagation()}>
              {item.project_id ? (
                <div>
                  <IdLink id={item.project_id} onClick={onOpenDetail} />
                  {item._project_name && <span className="block text-[10px] text-[#9B9589]">{item._project_name}</span>}
                </div>
              ) : <span className="text-[10px] text-[#D8D3CB]">—</span>}
            </td>
            <td className="px-4 py-3 hidden lg:table-cell" onClick={e => e.stopPropagation()}>
              {item.author_id?.startsWith('AINAA-') ? (
                <div>
                  <IdLink id={item.author_id} onClick={onOpenDetail} />
                  {(item.author_name || item._author_name) && <span className="block text-[10px] text-[#9B9589]">{item.author_name || item._author_name}</span>}
                </div>
              ) : (
                <span className="text-xs text-[#4A4640]">{item.author_name || item._author_name || item.author_id || '—'}</span>
              )}
            </td>
            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(item.ainaa_id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-entity-${item.ainaa_id}`}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════════════════
   LISTS TABLE — with project + org
   ═══════════════════════════════════════════ */
const ListsTable = ({ data, loading, onDelete, onOpenDetail }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="entity-table-lists">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <Th>AINAA ID</Th>
          <Th>Title</Th>
          <Th className="hidden sm:table-cell">Type</Th>
          <Th className="hidden md:table-cell">Project</Th>
          <Th className="hidden lg:table-cell">Organization</Th>
          <Th className="hidden lg:table-cell">Members</Th>
          <Th align="right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={7} className="text-center py-12 text-[#9B9589]">Loading...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={7} className="text-center py-12 text-[#9B9589]">No Lists found</td></tr>
        ) : data.map((item) => (
          <tr key={item.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer" onClick={() => onOpenDetail(item.ainaa_id)} data-testid={`list-row-${item.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{item.ainaa_id}</span>
            </td>
            <td className="px-4 py-3"><span className="text-sm font-semibold text-[#0F1B2D]">{item.title}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200">{item.type}</span>
            </td>
            <td className="px-4 py-3 hidden md:table-cell" onClick={e => e.stopPropagation()}>
              {item.project_id ? (
                <div>
                  <IdLink id={item.project_id} onClick={onOpenDetail} />
                  {item._project_name && <span className="block text-[10px] text-[#9B9589]">{item._project_name}</span>}
                </div>
              ) : <span className="text-[10px] text-[#D8D3CB]">—</span>}
            </td>
            <td className="px-4 py-3 hidden lg:table-cell" onClick={e => e.stopPropagation()}>
              {item.org_id ? (
                <div>
                  <IdLink id={item.org_id} onClick={onOpenDetail} />
                  {item._org_name && <span className="block text-[10px] text-[#9B9589]">{item._org_name}</span>}
                </div>
              ) : <span className="text-[10px] text-[#D8D3CB]">—</span>}
            </td>
            <td className="px-4 py-3 hidden lg:table-cell">
              <span className="text-xs text-[#4A4640]">{item.items?.length || 0}</span>
            </td>
            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(item.ainaa_id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-entity-${item.ainaa_id}`}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════════════════
   FILES TABLE — with project
   ═══════════════════════════════════════════ */
const FilesTable = ({ data, loading, onDelete, onOpenDetail }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="entity-table-files">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <Th>AINAA ID</Th>
          <Th>Name</Th>
          <Th className="hidden sm:table-cell">Type</Th>
          <Th className="hidden md:table-cell">Project</Th>
          <Th className="hidden lg:table-cell">Size</Th>
          <Th align="right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={6} className="text-center py-12 text-[#9B9589]">Loading...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={6} className="text-center py-12 text-[#9B9589]">No Files found</td></tr>
        ) : data.map((item) => (
          <tr key={item.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer" onClick={() => onOpenDetail(item.ainaa_id)} data-testid={`file-row-${item.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{item.ainaa_id}</span>
            </td>
            <td className="px-4 py-3"><span className="text-sm font-semibold text-[#0F1B2D]">{item.name}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <span className="text-xs text-[#4A4640]">{item.type}</span>
            </td>
            <td className="px-4 py-3 hidden md:table-cell" onClick={e => e.stopPropagation()}>
              {item.project_id ? (
                <div>
                  <IdLink id={item.project_id} onClick={onOpenDetail} />
                  {item._project_name && <span className="block text-[10px] text-[#9B9589]">{item._project_name}</span>}
                </div>
              ) : <span className="text-[10px] text-[#D8D3CB]">—</span>}
            </td>
            <td className="px-4 py-3 hidden lg:table-cell">
              <span className="text-xs text-[#9B9589]">{item.size_bytes ? `${(item.size_bytes / 1024).toFixed(1)} KB` : '—'}</span>
            </td>
            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(item.ainaa_id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-entity-${item.ainaa_id}`}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════════════════
   ORGANIZATIONS TABLE — with click-through
   ═══════════════════════════════════════════ */
const OrgsTable = ({ data, loading, onDelete, onOpenDetail }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="entity-table-organizations">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <Th>AINAA ID</Th>
          <Th>Name</Th>
          <Th className="hidden sm:table-cell">Type</Th>
          <Th className="hidden md:table-cell">Industry</Th>
          <Th className="hidden lg:table-cell">Created</Th>
          <Th align="right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={6} className="text-center py-12 text-[#9B9589]">Loading...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={6} className="text-center py-12 text-[#9B9589]">No Organizations found</td></tr>
        ) : data.map((item) => (
          <tr key={item.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer" onClick={() => onOpenDetail(item.ainaa_id)} data-testid={`org-row-${item.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{item.ainaa_id}</span>
            </td>
            <td className="px-4 py-3"><span className="text-sm font-semibold text-[#0F1B2D]">{item.name}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <span className="text-xs text-[#4A4640]">{item.type}</span>
            </td>
            <td className="px-4 py-3 hidden md:table-cell">
              <span className="text-xs text-[#4A4640]">{item.industry || '—'}</span>
            </td>
            <td className="px-4 py-3 hidden lg:table-cell">
              <span className="text-xs text-[#9B9589]">{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</span>
            </td>
            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(item.ainaa_id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-entity-${item.ainaa_id}`}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ═══════════════════════════════════════════
   LINKS TABLE
   ═══════════════════════════════════════════ */
const LinksTable = ({ data, loading, onDelete, onOpenDetail }) => (
  <div className="border border-[#D8D3CB] bg-white overflow-x-auto">
    <table className="w-full" data-testid="entity-table-links">
      <thead>
        <tr className="border-b-2 border-[#D8D3CB]">
          <Th>AINAA ID</Th>
          <Th>Title</Th>
          <Th className="hidden sm:table-cell">Type</Th>
          <Th className="hidden md:table-cell">URL</Th>
          <Th className="hidden lg:table-cell">Entity</Th>
          <Th align="right">Actions</Th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={6} className="text-center py-12 text-[#9B9589]">Loading...</td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={6} className="text-center py-12 text-[#9B9589]">No Links found</td></tr>
        ) : data.map((item) => (
          <tr key={item.ainaa_id} className="border-b border-[#D8D3CB]/50 hover:bg-[#EDECEA]/30 cursor-pointer" onClick={() => onOpenDetail(item.ainaa_id)} data-testid={`link-row-${item.ainaa_id}`}>
            <td className="px-4 py-3">
              <span className="text-[11px] font-mono font-bold text-[#C4A35A]">{item.ainaa_id}</span>
            </td>
            <td className="px-4 py-3"><span className="text-sm font-semibold text-[#0F1B2D]">{item.title}</span></td>
            <td className="px-4 py-3 hidden sm:table-cell">
              <LinkTypeBadge type={item.link_type} />
            </td>
            <td className="px-4 py-3 hidden md:table-cell" onClick={e => e.stopPropagation()}>
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline truncate max-w-[200px] block" title={item.url}>{item.url}</a>
            </td>
            <td className="px-4 py-3 hidden lg:table-cell" onClick={e => e.stopPropagation()}>
              <div>
                <IdLink id={item.entity_id} onClick={onOpenDetail} />
                {item._entity_name && <span className="block text-[10px] text-[#9B9589]">{item._entity_name}</span>}
              </div>
            </td>
            <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(item.ainaa_id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors" data-testid={`delete-entity-${item.ainaa_id}`}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const LinkTypeBadge = ({ type }) => {
  const colors = {
    imdb: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    instagram: 'bg-pink-50 text-pink-700 border-pink-200',
    linkedin: 'bg-blue-50 text-blue-700 border-blue-200',
    twitter: 'bg-sky-50 text-sky-700 border-sky-200',
    website: 'bg-gray-50 text-gray-600 border-gray-200',
    portfolio: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    trailer: 'bg-red-50 text-red-700 border-red-200',
    showreel: 'bg-purple-50 text-purple-700 border-purple-200',
    file: 'bg-amber-50 text-amber-700 border-amber-200',
    article: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 border ${colors[type] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      {type?.replace(/_/g, ' ') || 'other'}
    </span>
  );
};

/* ═══════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════ */
const Th = ({ children, align = 'left', className = '' }) => (
  <th className={`text-${align} px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#9B9589] ${className}`}>{children}</th>
);

const StatusPill = ({ status }) => {
  const colors = {
    planning: 'bg-gray-50 text-gray-600 border-gray-200',
    pre_production: 'bg-amber-50 text-amber-700 border-amber-200',
    production: 'bg-blue-50 text-blue-700 border-blue-200',
    post_production: 'bg-purple-50 text-purple-700 border-purple-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    draft: 'bg-gray-50 text-gray-600 border-gray-200',
    review: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    locked: 'bg-red-50 text-red-700 border-red-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 border ${colors[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      {status?.replace(/_/g, ' ') || '—'}
    </span>
  );
};

const CreateForm = ({ entityType, fields, onCreated }) => {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post(`/${entityType}`, formData);
      toast.success('Created');
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-[#D8D3CB] bg-white p-5 mb-5" data-testid="entity-create-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        {fields.map(f => (
          <div key={f}>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold block mb-1">{f.replace(/_/g, ' ')}</label>
            <Input value={formData[f] || ''} onChange={(e) => setFormData(d => ({...d, [f]: e.target.value}))}
              className="input-ainaa h-9 text-sm" placeholder={f.replace(/_/g, ' ')}
              required={f === fields[0]} data-testid={`create-field-${f}`} />
          </div>
        ))}
      </div>
      <button type="submit" disabled={submitting} className="btn-ainaa-primary px-5 py-2 text-xs font-semibold uppercase tracking-wider" data-testid="entity-submit-btn">
        {submitting ? 'Creating...' : 'Create'}
      </button>
    </form>
  );
};

export default EntitiesPanel;
